import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { unitLabels } from '../utils/units';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CostOfNonComplianceCharts from './CostOfNonComplianceCharts';
import CostOfNonComplianceSettings, { CostComplianceConfig, getDefaultConfig } from './CostOfNonComplianceSettings';
import MeterHealthModal from './MeterHealthModal';
import CalInsightButton from './CalInsightButton';

interface NonComplianceItem {
  meter_product_id: string;
  meter_id: string;
  product_type: string;
  flow_label?: string | null;      // 'HIGH'/'LOW'/'MID<n>' for multi-flow tracks
  product_display?: string;        // flow-aware label e.g. "KJETA1 (LOW)"
  display_name: string;
  current_meter_factor: number;
  drift_percent: number;
  days_out_of_compliance: number;
  average_annual_flow: number;
  affected_volume: number;
  product_price: number;
  financial_impact: number;
  annual_impact: number;
  daily_impact?: number;
  impact_type: 'Loss' | 'Gain' | 'In Compliance';
  meter_direction: string;
  compliance_status?: 'in_compliance' | 'loss' | 'gain';
  last_conforming_date: string | null;
  historical_loss?: number;  // Actual historical loss calculated by backend
  historical_gain?: number;  // Actual historical gain calculated by backend
}

interface CostSummary {
  total_meters: number;
  meters_in_compliance: number;
  meters_with_losses: number;
  meters_with_gains: number;  // Backend sends as gains, display as exposures
  total_meters_out: number;
  total_period_losses?: number;  // Actual historical losses
  total_period_gains?: number;   // Actual historical gains (displayed as exposures)
  net_period_impact?: number;     // Net historical impact
  total_annual_losses: number;    // Projected annual (for backward compatibility)
  total_annual_gains: number;  // Display as exposures
  net_annual_impact: number;
  total_daily_losses?: number;
  total_daily_gains?: number;  // Display as exposures
  net_daily_impact?: number;
  time_range_days?: number;
  period_start?: string;
  period_end?: string;
  last_updated: string;
}

interface CostOfNonComplianceProps {
  isDashboard?: boolean;
}

const CostOfNonCompliance: React.FC<CostOfNonComplianceProps> = ({ isDashboard = false }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<NonComplianceItem[]>([]);
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [timeRange, setTimeRange] = useState(30); // Default 30 days
  const [dailyTrend, setDailyTrend] = useState<any[]>([]);
  const [selectedMeterProductId, setSelectedMeterProductId] = useState<string | null>(null);
  const { getIdTokenClaims } = useAuth0();

  // Load config from localStorage or use defaults (only for dashboard mode)
  const [config, setConfig] = useState<CostComplianceConfig>(() => {
    // Always show everything in sidebar view
    if (!isDashboard) {
      return getDefaultConfig(false);
    }

    // Dashboard mode: load saved config or defaults
    const storageKey = 'costComplianceConfig_dashboard';
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return getDefaultConfig(true);
      }
    }
    return getDefaultConfig(true);
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/cost-of-noncompliance`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          },
          params: {
            time_range: timeRange
          }
        }
      );

      const data = response.data;
      // Use all_meters if available (new API), otherwise fall back to noncompliance_items
      setItems(data.all_meters || data.noncompliance_items || []);
      setSummary(data.summary || null);
      // Store daily trend data if available
      if (data.daily_trend) {
        setDailyTrend(data.daily_trend);
      }
    } catch (error) {
      console.error('Error fetching cost of non-compliance:', error);
    } finally {
      setLoading(false);
    }
  }, [getIdTokenClaims, timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save config to localStorage whenever it changes (only for dashboard mode)
  const handleConfigChange = useCallback((newConfig: CostComplianceConfig) => {
    setConfig(newConfig);
    if (isDashboard) {
      const storageKey = 'costComplianceConfig_dashboard';
      localStorage.setItem(storageKey, JSON.stringify(newConfig));
    }
  }, [isDashboard]);

  const formatCurrency = (value: number) => {
    const absValue = Math.abs(value);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(absValue);

    if (value < 0) {
      return `(${formatted})`;
    }
    return formatted;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Settings Dialog */}
      <CostOfNonComplianceSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        config={config}
        onConfigChange={handleConfigChange}
        isDashboard={isDashboard}
      />

      {/* Dashboard Header with Settings - Always visible in dashboard mode */}
      {isDashboard && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5, px: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Cost of Non-Compliance</Typography>
              <Box>
                <Tooltip title="Customize Dashboard">
                  <IconButton onClick={() => setSettingsOpen(true)} size="small" color="primary">
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards - Two rows: Annual and Daily */}
      {(isDashboard ? config.showSummaryCards : true) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* First Row - Annual Impacts */}
        <Grid item xs={12} md={3}>
          <Card sx={{ position: 'relative' }}>
            <CalInsightButton
              componentType="fleet-health"
              data={{
                totalMeters: summary?.total_meters || 0,
                criticalCount: summary?.meters_with_losses || 0,
                warningCount: summary?.meters_with_gains || 0,
                complianceRate: summary ? ((summary.meters_in_compliance / summary.total_meters) * 100).toFixed(1) : '0',
                timeRange: timeRange
              }}
            />
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Meter Status
              </Typography>
              <Box display="flex" gap={1} alignItems="center">
                <Box>
                  <Typography variant="caption" color="success.main">
                    In Compliance
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {summary?.meters_in_compliance || 0}
                  </Typography>
                </Box>
                <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 1 }}>
                  <Typography variant="caption" color="error">
                    Loss
                  </Typography>
                  <Typography variant="h6" color="error">
                    {summary?.meters_with_losses || 0}
                  </Typography>
                </Box>
                <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 1 }}>
                  <Typography variant="caption" color="warning.main">
                    Exposure
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {summary?.meters_with_gains || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ position: 'relative' }}>
            <CalInsightButton
              componentType="summary-stats"
              data={{
                annualLoss: summary?.total_annual_losses || 0,
                nonCompliantCount: (summary?.meters_with_losses || 0) + (summary?.meters_with_gains || 0),
                averageOutsideControl: items.length ? (items.reduce((sum, item) => sum + Math.abs(item.drift_percent), 0) / items.length).toFixed(2) : 0,
                recoveryPotential: Math.abs(summary?.total_annual_losses || 0) * 0.8,
                timeRange: timeRange
              }}
            />
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Annual Losses
              </Typography>
              <Typography variant="h5" color="error">
                {formatCurrency(summary?.total_annual_losses || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ position: 'relative' }}>
            <CalInsightButton
              componentType="annual-exposure"
              data={{
                annualExposure: summary?.total_annual_gains || 0,
                meterCount: summary?.meters_with_gains || 0,
                averageGain: summary?.meters_with_gains ?
                  ((summary?.total_annual_gains || 0) / summary.meters_with_gains) : 0,
                timeRange: timeRange
              }}
            />
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Annual Exposure
              </Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrency(summary?.total_annual_gains || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ position: 'relative' }}>
            <CalInsightButton
              componentType="annual-cost"
              data={{
                totalCost: Math.abs((summary?.total_annual_losses || 0) - (summary?.total_annual_gains || 0)),
                losses: summary?.total_annual_losses || 0,
                gains: summary?.total_annual_gains || 0,
                netImpact: (summary?.total_annual_losses || 0) > (summary?.total_annual_gains || 0) ? 'loss' : 'gain',
                timeRange: timeRange
              }}
            />
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Annual Cost of Non-Compliance
              </Typography>
              <Typography
                variant="h5"
                color="error"
              >
                {formatCurrency(
                  Math.abs(summary?.total_annual_losses || 0) +
                  Math.abs(summary?.total_annual_gains || 0)
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Second Row - Daily Impacts */}
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: 'action.hover' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Today's Date
              </Typography>
              <Typography variant="h6">
                {new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: 'action.hover', position: 'relative' }}>
            <CalInsightButton
              componentType="daily-losses"
              data={{
                dailyLoss: summary?.total_daily_losses || 0,
                annualizedLoss: (summary?.total_daily_losses || 0) * 365,
                meterCount: summary?.meters_with_losses || 0,
                averageLossPerMeter: summary?.meters_with_losses ?
                  ((summary?.total_daily_losses || 0) / summary.meters_with_losses) : 0,
                timeRange: timeRange
              }}
            />
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Daily Losses
              </Typography>
              <Typography variant="h5" color="error">
                {formatCurrency(summary?.total_daily_losses || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: 'action.hover', position: 'relative' }}>
            <CalInsightButton
              componentType="daily-exposure"
              data={{
                dailyExposure: summary?.total_daily_gains || 0,
                annualizedExposure: (summary?.total_daily_gains || 0) * 365,
                meterCount: summary?.meters_with_gains || 0,
                averageGainPerMeter: summary?.meters_with_gains ?
                  ((summary?.total_daily_gains || 0) / summary.meters_with_gains) : 0,
                timeRange: timeRange
              }}
            />
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Daily Exposure
              </Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrency(summary?.total_daily_gains || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: 'action.hover', position: 'relative' }}>
            <CalInsightButton
              componentType="daily-cost"
              data={{
                dailyCost: Math.abs((summary?.total_daily_losses || 0) - (summary?.total_daily_gains || 0)),
                dailyLosses: summary?.total_daily_losses || 0,
                dailyGains: summary?.total_daily_gains || 0,
                netImpact: (summary?.total_daily_losses || 0) > (summary?.total_daily_gains || 0) ? 'loss' : 'gain',
                annualizedCost: Math.abs((summary?.total_daily_losses || 0) - (summary?.total_daily_gains || 0)) * 365,
                timeRange: timeRange
              }}
            />
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Daily Cost of Non-Compliance
              </Typography>
              <Typography
                variant="h5"
                color="error"
              >
                {formatCurrency(
                  Math.abs(summary?.total_daily_losses || 0) +
                  Math.abs(summary?.total_daily_gains || 0)
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        </Grid>
      )}

      {/* Detailed Table */}
      {(isDashboard ? config.showDetailsTable : true) && (
        <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {isDashboard ? 'Top 5 Losses' : 'Cost of Non-Compliance Details'}
            </Typography>
            <Box>
              {!isDashboard && (
                <>
                  <Tooltip title="Refresh data">
                    <IconButton onClick={fetchData} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Calculations based on meter drift from control limits and average annual flow rates">
                    <IconButton size="small">
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Meter</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Current MF</TableCell>
                  <TableCell align="right">% Outside Control</TableCell>
                  <TableCell align="right">Days Out</TableCell>
                  <TableCell align="right">Daily Flow ({unitLabels().volume})</TableCell>
                  <TableCell align="right">Daily Impact</TableCell>
                  <TableCell align="right">Annual Impact</TableCell>
                  <TableCell align="center">Type</TableCell>
                  <TableCell align="center">Direction</TableCell>
                  <TableCell align="center">Insights</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No meters found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (isDashboard
                    ? items
                        .filter(item => item.impact_type === 'Loss')
                        .sort((a, b) => Math.abs(b.annual_impact) - Math.abs(a.annual_impact))
                        .slice(0, 5)
                    : items
                        .sort((a, b) => {
                          // First show losses
                          if (a.compliance_status === 'loss' && b.compliance_status !== 'loss') return -1;
                          if (a.compliance_status !== 'loss' && b.compliance_status === 'loss') return 1;

                          // Then show exposures (formerly gains)
                          if (a.compliance_status === 'gain' && b.compliance_status === 'in_compliance') return -1;
                          if (a.compliance_status === 'in_compliance' && b.compliance_status === 'gain') return 1;

                          // Within losses: highest to lowest (bigger loss first)
                          if (a.compliance_status === 'loss' && b.compliance_status === 'loss') {
                            return Math.abs(b.annual_impact) - Math.abs(a.annual_impact);
                          }

                          // Within exposures: highest to lowest
                          if (a.compliance_status === 'gain' && b.compliance_status === 'gain') {
                            return Math.abs(b.annual_impact) - Math.abs(a.annual_impact);
                          }

                          return 0;
                        })
                  ).map((item) => (
                    <TableRow
                      key={item.meter_product_id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        backgroundColor:
                          item.compliance_status === 'in_compliance' ? 'rgba(76, 175, 80, 0.08)' :  // Green
                          item.compliance_status === 'loss' ? 'rgba(244, 67, 54, 0.08)' :  // Red
                          item.compliance_status === 'gain' ? 'rgba(255, 152, 0, 0.08)' :  // Orange
                          'transparent',
                        '&:hover': {
                          backgroundColor:
                            item.compliance_status === 'in_compliance' ? 'rgba(76, 175, 80, 0.15)' :
                            item.compliance_status === 'loss' ? 'rgba(244, 67, 54, 0.15)' :
                            item.compliance_status === 'gain' ? 'rgba(255, 152, 0, 0.15)' :
                            'action.hover'
                        }
                      }}
                      onClick={() => setSelectedMeterProductId(item.meter_product_id)}
                    >
                      <TableCell>{item.meter_id}</TableCell>
                      <TableCell>{item.product_display || item.product_type}</TableCell>
                      <TableCell align="right">{item.current_meter_factor.toFixed(4)}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          {/* Show drift direction arrow based on drift percentage */}
                          {item.drift_percent > 0 ? (
                            <Tooltip title="Positive drift (meter reading high relative to baseline)">
                              <ArrowUpwardIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                            </Tooltip>
                          ) : item.drift_percent < 0 ? (
                            <Tooltip title="Negative drift (meter reading low relative to baseline)">
                              <ArrowDownwardIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                            </Tooltip>
                          ) : null}
                          {item.drift_percent.toFixed(2)}%
                        </Box>
                      </TableCell>
                      <TableCell align="right">{item.days_out_of_compliance}</TableCell>
                      <TableCell align="right">{formatNumber(item.average_annual_flow / 365)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          color={item.impact_type === 'Loss' ? 'error' : 'success.main'}
                          fontWeight="medium"
                        >
                          {formatCurrency(item.annual_impact / 365)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          color={item.impact_type === 'Loss' ? 'error' : 'success.main'}
                          fontWeight="medium"
                        >
                          {formatCurrency(item.annual_impact)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.impact_type === 'Gain' ? 'Exposure' : item.impact_type}
                          size="small"
                          color={
                            item.impact_type === 'Loss' ? 'error' :
                            item.impact_type === 'Gain' ? 'warning' :
                            'success'
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={
                          item.compliance_status === 'in_compliance' ?
                            "Within control limits - no financial impact" :
                          item.meter_direction === 'inbound' ?
                            (item.drift_percent > 0 ?
                              "Inbound + Positive drift = Paying for more than received (Loss)" :
                              "Inbound + Negative drift = Getting more than paid for (Exposure)") :
                            (item.drift_percent > 0 ?
                              "Outbound + Positive drift = Charging for more than delivered (Exposure)" :
                              "Outbound + Negative drift = Giving away product (Loss)")
                        }>
                          <Chip
                            label={item.meter_direction}
                            size="small"
                            variant="outlined"
                            sx={{ cursor: 'help' }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <CalInsightButton
                          componentType="meter-specific"
                          data={{
                            meterId: item.meter_id,
                            product: item.product_type,
                            meterFactor: item.current_meter_factor,
                            percentOutsideControl: item.drift_percent,
                            daysOut: item.days_out_of_compliance,
                            dailyFlow: item.average_annual_flow / 365,
                            dailyImpact: item.annual_impact / 365,
                            annualImpact: item.annual_impact,
                            impactType: item.impact_type,
                            direction: item.meter_direction
                          }}
                          position="inline"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {isDashboard && items.filter(item => item.impact_type === 'Loss').length > 5 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
              Showing top 5 losses. View full details in the Cost of Non-Compliance tab.
            </Typography>
          )}

          {summary && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Last updated: {new Date(summary.last_updated).toLocaleString()}
            </Typography>
          )}
        </CardContent>
        </Card>
      )}

      {/* Visualizations - Show based on mode and config */}
      {items.length > 0 && (!isDashboard || config.charts.some(c => c.enabled)) && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Loss Analysis & Trends
          </Typography>
          <CostOfNonComplianceCharts
            items={items}
            summary={summary}
            config={isDashboard ? config : undefined}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
            dailyTrend={dailyTrend}
            onMeterClick={(meterProductId) => setSelectedMeterProductId(meterProductId)}
          />
        </Box>
      )}

      {/* Empty State for Dashboard when everything is hidden */}
      {isDashboard && !config.showSummaryCards && !config.showDetailsTable && !config.charts.some(c => c.enabled) && (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              No components are currently visible. Click the settings icon above to customize your view.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Meter Health Modal */}
      <MeterHealthModal
        open={!!selectedMeterProductId}
        onClose={() => setSelectedMeterProductId(null)}
        meterProductId={selectedMeterProductId || ''}
      />
    </Box>
  );
};

export default CostOfNonCompliance;
// Trigger Amplify deployment - Thu Jun 25 2026
